import { useMemo, useState } from 'react';
import Modal from '../Modal.tsx';
import type { TeamSyncResult } from '../../../shared/api.ts';

/**
 * The team-sync preview when somebody might have changed their name.
 *
 * A surname change reads to the sync as one person leaving and another
 * joining — same portrait, same job, new slug — and acting on that deletes a
 * record and its history. The server refuses to guess which add pairs with
 * which removal, so this dialog asks.
 *
 * It is a component rather than a `setConfirm` body because ConfirmModal holds
 * no state and binds Enter globally: pairing inside it would fire the sync
 * mid-tick.
 */
export default function TeamSyncModal({
  diff,
  busy,
  onCancel,
  onConfirm,
}: {
  diff: TeamSyncResult;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (renames: { bankId: string; siteId: string }[]) => void;
}) {
  // bank id → the site id they became. Absent means "they really did leave".
  const [picked, setPicked] = useState<Record<string, string>>({});

  const pairs = Object.entries(picked).filter(([, siteId]) => siteId);
  const pickedSite = new Set(pairs.map(([, siteId]) => siteId));
  const nameOf = (siteId: string) =>
    diff.renameCandidates.flatMap((c) => c.options).find((o) => o.id === siteId)?.name || siteId;

  // Pairing moves a person out of Add and out of Remove without another round
  // trip to the site, so the diff below always describes what Sync now will do.
  const shown = useMemo(
    () => ({
      added: diff.added.filter((a) => !pickedSite.has(a.id)),
      removed: diff.removed.filter((r) => !picked[r.id]),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pickedSite is rebuilt each render from `picked`
    [diff, picked],
  );

  const groups = [
    { title: 'Add', list: shown.added.map((p) => p.name), why: 'photo downloaded from the site' },
    { title: 'Update', list: diff.updated.map((p) => p.name), why: 'name, role and departments from the site' },
    {
      title: 'Rename',
      list: [
        ...diff.renamed.map((r) => `${r.from} → ${r.to}`),
        ...pairs.map(([bankId, siteId]) => {
          const was = diff.renameCandidates.find((c) => c.bank.id === bankId)?.bank.name || bankId;
          return `${was} → ${nameOf(siteId)}`;
        }),
      ],
      why: 'same person, new name — record, photo and decks all kept',
    },
    { title: 'Remove', list: shown.removed.map((p) => p.name), why: 'no longer on the site' },
    {
      title: 'Clean up',
      list: (diff.removedDuplicates || []).map((p) => p.name),
      why: 'duplicate bank entries (each person keeps their synced card)',
    },
  ].filter((g) => g.list.length);

  return (
    <Modal
      title="Sync people from rkh.co.uk?"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onConfirm(pairs.map(([bankId, siteId]) => ({ bankId, siteId })))}
            disabled={busy}
          >
            Sync now
          </button>
        </>
      }
    >
      {diff.renameCandidates.length > 0 && (
        <>
          <p className="bank-sync__note">
            {diff.renameCandidates.length === 1
              ? 'The site has stopped listing this person. Have they changed their name rather than left?'
              : 'The site has stopped listing these people. Has any of them changed their name rather than left?'}
          </p>
          <ul className="cat-picker" role="list">
            {diff.renameCandidates.map((c) => (
              <li key={c.bank.id}>
                <label className="cat-picker__row">
                  <span>{c.bank.name}</span>
                  <select
                    className="modal__input"
                    value={picked[c.bank.id] || ''}
                    onChange={(e) => setPicked((prev) => ({ ...prev, [c.bank.id]: e.target.value }))}
                  >
                    <option value="">left the agency</option>
                    {c.options.map((o) => (
                      // Someone already spoken for elsewhere in this dialog
                      // can't also be this person.
                      <option key={o.id} value={o.id} disabled={pickedSite.has(o.id) && picked[c.bank.id] !== o.id}>
                        is now {o.name}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
          <p className="muted bank-sync__note" style={{ marginTop: '0.5rem', marginBottom: '0.8rem' }}>
            {pairs.length
              ? 'A renamed person keeps their record, photo and every deck they are already in.'
              : 'Left as they are, their bank entries are deleted and the new names are added as new people.'}
          </p>
        </>
      )}
      <div className="bank-sync">
        {groups.map(({ title, list, why }) => (
          <section key={title}>
            <strong>
              {title} {list.length}
            </strong>
            <span className="muted bank-sync__why">: {why}</span>
            <p className="muted bank-sync__names">{list.join(', ')}</p>
          </section>
        ))}
        {diff.errors.length > 0 && (
          <section>
            <strong>Problems</strong>
            <p className="muted bank-sync__error">{diff.errors.join('; ')}</p>
          </section>
        )}
      </div>
    </Modal>
  );
}
